import React, { Component } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';

class TipDistributionModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedEmployees: {}, // { employeeId: tipAmount }
      errors: {},
      distributionMode: 'even', // 'even' or 'manual'
      checkedEmployees: [] // Array of employee IDs for even split mode
    };
  }

  handleTipAmountChange = (employeeId, amount) => {
    const { selectedEmployees } = this.state;
    const numericAmount = parseFloat(amount);

    if (amount === '' || numericAmount === 0) {
      // Remove employee if amount is cleared or zero
      const newSelected = { ...selectedEmployees };
      delete newSelected[employeeId];
      this.setState({ selectedEmployees: newSelected });
    } else if (!isNaN(numericAmount) && numericAmount > 0) {
      this.setState({
        selectedEmployees: {
          ...selectedEmployees,
          [employeeId]: numericAmount
        },
        errors: {
          ...this.state.errors,
          [employeeId]: null
        }
      });
    } else {
      this.setState({
        errors: {
          ...this.state.errors,
          [employeeId]: 'Invalid amount'
        }
      });
    }
  };

  handleConfirm = () => {
    const { selectedEmployees } = this.state;
    const { employees, crypto, onConfirm } = this.props;

    // Build employee_tips array
    const employeeTips = Object.entries(selectedEmployees).map(([employeeId, tipAmount]) => {
      const employee = employees.find(e => e.id.toString() === employeeId);
      const cryptoLower = crypto.toLowerCase();
      const addressField = `${cryptoLower}_address`;

      return {
        employee_id: parseInt(employeeId),
        employee_name: employee.employee_name,
        address: employee[addressField],
        tip_amount: tipAmount
      };
    });

    onConfirm(employeeTips);
  };

  handleSkip = () => {
    const { onSkip } = this.props;
    onSkip();
  };

  getTotalTips = () => {
    const { selectedEmployees } = this.state;
    return Object.values(selectedEmployees).reduce((sum, amount) => sum + amount, 0);
  };

  handleModeChange = (mode) => {
    const { selectedEmployees } = this.state;
    const hasManualAmounts = Object.keys(selectedEmployees).length > 0;

    // If switching from manual to even and manual amounts exist, warn user
    if (mode === 'even' && hasManualAmounts && this.state.distributionMode === 'manual') {
      if (!window.confirm('Switching to Even Split will clear your manual amounts. Continue?')) {
        return;
      }
      // Clear manual amounts when switching to even split
      this.setState({
        distributionMode: mode,
        selectedEmployees: {},
        checkedEmployees: []
      });
    } else if (mode === 'manual' && this.state.checkedEmployees.length > 0) {
      // Switching from even to manual, clear checked employees
      this.setState({
        distributionMode: mode,
        selectedEmployees: {},
        checkedEmployees: []
      });
    } else {
      this.setState({ distributionMode: mode });
    }
  };

  handleEmployeeCheck = (employeeId, checked) => {
    const { checkedEmployees } = this.state;
    const { totalTipsAvailable } = this.props;

    let newChecked;
    if (checked) {
      newChecked = [...checkedEmployees, employeeId];
    } else {
      newChecked = checkedEmployees.filter(id => id !== employeeId);
    }

    this.setState({ checkedEmployees: newChecked }, () => {
      // Auto-calculate even split
      this.calculateEvenSplit(newChecked, totalTipsAvailable);
    });
  };

  calculateEvenSplit = (checkedEmployeeIds, totalTipsAvailable) => {
    if (checkedEmployeeIds.length === 0) {
      this.setState({ selectedEmployees: {} });
      return;
    }

    // CRITICAL FIX: Use satoshi-level arithmetic to avoid rounding errors
    // that cause "Failed to create transaction skeleton" errors

    // Convert to satoshis (smallest unit)
    const totalSatoshis = Math.floor(totalTipsAvailable * 100000000);
    const numEmployees = checkedEmployeeIds.length;

    // Divide satoshis evenly, rounding DOWN to ensure total never exceeds available
    const satoshisPerEmployee = Math.floor(totalSatoshis / numEmployees);

    // Calculate remainder satoshis that couldn't be evenly divided
    const remainderSatoshis = totalSatoshis - (satoshisPerEmployee * numEmployees);

    // Convert back to crypto units (DOGE/BTC/LTC)
    const baseAmountPerEmployee = satoshisPerEmployee / 100000000;
    const remainderAmount = remainderSatoshis / 100000000;

    const newSelectedEmployees = {};

    // Give base amount to all employees
    checkedEmployeeIds.forEach((employeeId, index) => {
      // Give the first employee the remainder satoshis to avoid dust
      const amount = index === 0
        ? baseAmountPerEmployee + remainderAmount
        : baseAmountPerEmployee;
      newSelectedEmployees[employeeId] = amount;
    });

    this.setState({ selectedEmployees: newSelectedEmployees });
  };

  // Helper: Format address for display
  formatAddress = (address) => {
    if (!address) return 'N/A';
    // Show first 6 and last 4 characters
    if (address.length > 15) {
      return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    }
    return address;
  };

  // Helper: Format amount with appropriate decimals
  formatAmount = (amount, isSmallScreen) => {
    const decimals = isSmallScreen ? 6 : 8;
    return amount.toFixed(decimals);
  };

  render() {
    const { show, onHide, employees, crypto, loading, totalTipsAvailable = 0 } = this.props;
    const { selectedEmployees, errors, distributionMode, checkedEmployees } = this.state;
    const totalTips = this.getTotalTips();
    const hasSelection = Object.keys(selectedEmployees).length > 0;
    const remaining = totalTipsAvailable - totalTips;

    // CRITICAL: Use satoshi-level comparison to avoid floating-point precision errors
    // Converting to satoshis (smallest unit) ensures accurate comparison
    const totalTipsSatoshis = Math.round(totalTips * 100000000);
    const availableSatoshis = Math.round(totalTipsAvailable * 100000000);
    const exceedsAvailable = totalTipsSatoshis > availableSatoshis;

    // Detect screen size and orientation
    const isSmallScreen = window.innerWidth < 600;
    const isLandscape = window.innerWidth > window.innerHeight;
    const shouldUseTwoColumns = isLandscape && employees && employees.length > 3;

    if (loading) {
      return (
        <Modal show={show} onHide={onHide} size="lg" centered>
          <Modal.Body className="text-center py-5">
            <h5>Loading employees...</h5>
          </Modal.Body>
        </Modal>
      );
    }

    if (!employees || employees.length === 0) {
      return (
        <Modal show={show} onHide={onHide} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Distribute Tips to Employees</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-info">
              No employees with {crypto} addresses found.
              <br />
              <small>Add employees at /employees before distributing tips.</small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleSkip}>
              Continue Without Tips
            </Button>
          </Modal.Footer>
        </Modal>
      );
    }

    return (
      <Modal show={show} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Distribute Tips to Employees ({crypto})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {totalTipsAvailable > 0 ? (
            <div className="alert alert-primary mb-3" style={{ fontSize: '16px' }}>
              <strong>💰 Tip Pool Available:</strong> {totalTipsAvailable.toFixed(8)} {crypto}
              <br />
              <small style={{ color: '#666' }}>
                (Collected from customer tips in {crypto} transactions)
              </small>
              {hasSelection && (
                <>
                  <br />
                  <span style={{ color: exceedsAvailable ? '#dc3545' : '#28a745' }}>
                    <strong>Remaining:</strong> {remaining.toFixed(8)} {crypto}
                  </span>
                </>
              )}
            </div>
          ) : (
            <div className="alert alert-warning mb-3">
              <strong>No customer tips collected yet.</strong> The {crypto} tip pool is empty.
            </div>
          )}

          <div className="alert alert-info mb-3">
            Enter tip amounts for employees to include in this payout. Tips will be deducted before calculating the retailer/platform split.
          </div>

          {/* Distribution Mode Selector */}
          <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            <div style={{ marginBottom: '10px', fontWeight: '600', fontSize: '15px' }}>Distribution Mode:</div>
            <Form.Check
              inline
              type="radio"
              label="Even Split (Recommended)"
              name="distributionMode"
              id="mode-even"
              checked={distributionMode === 'even'}
              onChange={() => this.handleModeChange('even')}
              style={{ fontSize: '14px', marginRight: '20px' }}
            />
            <Form.Check
              inline
              type="radio"
              label="Manual Entry"
              name="distributionMode"
              id="mode-manual"
              checked={distributionMode === 'manual'}
              onChange={() => this.handleModeChange('manual')}
              style={{ fontSize: '14px' }}
            />
            {distributionMode === 'even' && (
              <div className="mt-2" style={{ fontSize: '13px', color: '#6c757d' }}>
                ℹ️ Select employees who worked this shift. Tips will be split evenly among them.
              </div>
            )}
          </div>

          {shouldUseTwoColumns ? (
            // Landscape mode with 3+ employees: Two-column layout
            <div className="row">
              <div className="col-md-6">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      {distributionMode === 'even' && <th style={{ width: '50px' }}>✓</th>}
                      <th style={{ width: '40px' }}>#</th>
                      <th>Name</th>
                      <th style={{ width: isSmallScreen ? '80px' : '100px' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.slice(0, Math.ceil(employees.length / 2)).map((employee) => {
                      const cryptoLower = crypto.toLowerCase();
                      const addressField = `${cryptoLower}_address`;
                      const address = employee[addressField];
                      const currentAmount = selectedEmployees[employee.id] || '';
                      const hasError = errors[employee.id];
                      const isChecked = checkedEmployees.includes(employee.id);

                      return (
                        <tr key={employee.id}>
                          {distributionMode === 'even' && (
                            <td className="text-center">
                              <Form.Check
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => this.handleEmployeeCheck(employee.id, e.target.checked)}
                              />
                            </td>
                          )}
                          <td>{employee.employee_number || '-'}</td>
                          <td>
                            <strong style={{ fontSize: '14px' }}>{employee.employee_name}</strong>
                          </td>
                          <td style={{ fontSize: '13px', fontWeight: '600' }}>
                            {distributionMode === 'even' ? (
                              isChecked && currentAmount ? this.formatAmount(currentAmount, isSmallScreen) : '-'
                            ) : (
                              <Form.Control
                                type="number"
                                step="0.000001"
                                min="0"
                                placeholder="0.00"
                                value={currentAmount}
                                onChange={(e) => this.handleTipAmountChange(employee.id, e.target.value)}
                                isInvalid={hasError}
                                size="sm"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="col-md-6">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      {distributionMode === 'even' && <th style={{ width: '50px' }}>✓</th>}
                      <th style={{ width: '40px' }}>#</th>
                      <th>Name</th>
                      <th style={{ width: isSmallScreen ? '80px' : '100px' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.slice(Math.ceil(employees.length / 2)).map((employee) => {
                      const cryptoLower = crypto.toLowerCase();
                      const addressField = `${cryptoLower}_address`;
                      const address = employee[addressField];
                      const currentAmount = selectedEmployees[employee.id] || '';
                      const hasError = errors[employee.id];
                      const isChecked = checkedEmployees.includes(employee.id);

                      return (
                        <tr key={employee.id}>
                          {distributionMode === 'even' && (
                            <td className="text-center">
                              <Form.Check
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => this.handleEmployeeCheck(employee.id, e.target.checked)}
                              />
                            </td>
                          )}
                          <td>{employee.employee_number || '-'}</td>
                          <td>
                            <strong style={{ fontSize: '14px' }}>{employee.employee_name}</strong>
                          </td>
                          <td style={{ fontSize: '13px', fontWeight: '600' }}>
                            {distributionMode === 'even' ? (
                              isChecked && currentAmount ? this.formatAmount(currentAmount, isSmallScreen) : '-'
                            ) : (
                              <Form.Control
                                type="number"
                                step="0.000001"
                                min="0"
                                placeholder="0.00"
                                value={currentAmount}
                                onChange={(e) => this.handleTipAmountChange(employee.id, e.target.value)}
                                isInvalid={hasError}
                                size="sm"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            // Portrait or < 3 employees: Single-column layout
            <Table striped bordered hover size={isSmallScreen ? 'sm' : 'md'} responsive>
              <thead>
                <tr>
                  {distributionMode === 'even' && <th style={{ width: isSmallScreen ? '50px' : '60px' }}>✓</th>}
                  <th style={{ width: isSmallScreen ? '40px' : '60px' }}>#</th>
                  <th>Name</th>
                  {!isSmallScreen && <th>{crypto} Address</th>}
                  <th style={{ width: isSmallScreen ? '100px' : '150px' }}>
                    {distributionMode === 'even' ? 'Amount' : 'Tip'} ({crypto})
                  </th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => {
                  const cryptoLower = crypto.toLowerCase();
                  const addressField = `${cryptoLower}_address`;
                  const address = employee[addressField];
                  const currentAmount = selectedEmployees[employee.id] || '';
                  const hasError = errors[employee.id];
                  const isChecked = checkedEmployees.includes(employee.id);

                  return (
                    <tr key={employee.id}>
                      {distributionMode === 'even' && (
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => this.handleEmployeeCheck(employee.id, e.target.checked)}
                          />
                        </td>
                      )}
                      <td>{employee.employee_number || '-'}</td>
                      <td>
                        <strong style={{ fontSize: isSmallScreen ? '14px' : '15px' }}>
                          {employee.employee_name}
                        </strong>
                        {/* Show abbreviated address on small screens as subtitle */}
                        {isSmallScreen && address && (
                          <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                            {this.formatAddress(address)}
                          </div>
                        )}
                      </td>
                      {!isSmallScreen && (
                        <td className="small text-muted">
                          {this.formatAddress(address)}
                        </td>
                      )}
                      <td style={{ fontWeight: '600', fontSize: isSmallScreen ? '13px' : '14px' }}>
                        {distributionMode === 'even' ? (
                          <div style={{ padding: '6px 12px' }}>
                            {isChecked && currentAmount ? this.formatAmount(currentAmount, isSmallScreen) : '-'}
                          </div>
                        ) : (
                          <>
                            <Form.Control
                              type="number"
                              step="0.000001"
                              min="0"
                              placeholder="0.00"
                              value={currentAmount}
                              onChange={(e) => this.handleTipAmountChange(employee.id, e.target.value)}
                              isInvalid={hasError}
                              size={isSmallScreen ? 'sm' : 'md'}
                            />
                            {hasError && (
                              <Form.Control.Feedback type="invalid">
                                {hasError}
                              </Form.Control.Feedback>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {hasSelection && (
            <div className={`alert ${exceedsAvailable ? 'alert-danger' : 'alert-success'}`}>
              <strong>Total Tips:</strong> {totalTips.toFixed(2)} {crypto} for {Object.keys(selectedEmployees).length} employee(s)
              {exceedsAvailable && (
                <>
                  <br />
                  <strong>⚠️ Error:</strong> Total tips exceed available tips by {(totalTips - totalTipsAvailable).toFixed(2)} {crypto}
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.handleSkip}>
            Skip (No Tips)
          </Button>
          <Button
            variant="primary"
            onClick={this.handleConfirm}
            disabled={!hasSelection || exceedsAvailable}
          >
            {hasSelection ? `Distribute ${totalTips.toFixed(2)} ${crypto}` : 'Continue'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default TipDistributionModal;
